/**
 * TeacherDashboard — Panel para profesores con listado de alumnos y drill-down
 * a sus intentos. Solo se renderiza si authService.currentUser.role === 'teacher'.
 *
 * Dos vistas:
 *   1. Lista de alumnos (tabla) — ordenable por XP / actividad / nombre.
 *   2. Detalle de alumno — muestra progress + últimos intentos.
 */

import { authService } from '../auth/AuthService';
import { assignmentsService, type Assignment } from './AssignmentsService';
import { liveSession } from '../live/LiveSession';

interface StudentSummary {
  id: number;
  email: string;
  display_name: string;
  role: string;
  xp: number;
  level: number;
  cases_completed: number;
  cases_correct: number;
  accuracy: number;
  last_activity_at: string | null;
  teacher_id?: number | null;
}

interface AttemptRow {
  id: number;
  case_id: string;
  diagnosis_correct: boolean;
  triage_correct: boolean;
  score: number;
  duration_seconds: number;
  started_at: string;
  completed_at: string | null;
}

interface ProgressRow {
  user_id: number;
  xp: number;
  level: number;
  cases_completed: number;
  cases_correct: number;
  current_streak: number;
  longest_streak: number;
  last_activity_at: string | null;
}

type OrderBy = 'xp' | 'last_activity' | 'name';
type Tab = 'students' | 'analytics' | 'assignments' | 'logins' | 'debriefs' | 'live';

interface LoginEventRow {
  id: number;
  user_id: number | null;
  email: string;
  role: string | null;
  success: boolean;
  ip: string | null;
  user_agent: string | null;
  logged_at: string;
  error_reason: string | null;
}

interface DebriefRow {
  id: number;
  user_id: number;
  student_email: string;
  student_name: string;
  case_id: string | null;
  diagnosis: string | null;
  chief_complaint: string | null;
  duration_min: number;
  score_0_100: number;
  summary: string;
  strengths: string[];
  gaps: string[];
  pearls: string[];
  next_case: string | null;
  created_at: string;
}

interface CaseStat {
  case_id: string;
  attempts: number;
  accuracy: number;
  avg_duration_seconds: number;
}
interface DailyPoint {
  date: string;
  attempts: number;
  correct: number;
}
interface AccuracyBucket {
  label: string;
  students: number;
}
interface TeacherAnalyticsResponse {
  total_students: number;
  active_last_7_days: number;
  total_attempts: number;
  global_accuracy: number;
  avg_xp: number;
  top_cases: CaseStat[];
  daily_activity: DailyPoint[];
  accuracy_distribution: AccuracyBucket[];
}

const PANEL_ID = 'cgt-teacher-panel';
const STYLE_ID = 'cgt-teacher-styles';

export class TeacherDashboard {
  private root: HTMLElement | null = null;
  private orderBy: OrderBy = 'xp';
  private activeTab: Tab = 'students';

  /** Devuelve true si el usuario actual puede ver este dashboard. */
  isAvailable(): boolean {
    return authService.currentUser?.role === 'teacher';
  }

  async open(): Promise<void> {
    if (!this.isAvailable()) return;
    this.injectStyles();

    if (!this.root) {
      this.root = this.buildShell();
      document.body.appendChild(this.root);
      requestAnimationFrame(() => this.root?.classList.add('cgt-td-visible'));
    }
    if (this.activeTab === 'analytics') await this.renderAnalytics();
    else if (this.activeTab === 'assignments') await this.renderAssignments();
    else if (this.activeTab === 'logins') await this.renderLogins();
    else if (this.activeTab === 'debriefs') await this.renderDebriefs();
    else if (this.activeTab === 'live') await this.renderLive();
    else await this.renderStudentList();
  }

  private setTab(tab: Tab): void {
    this.activeTab = tab;
    this.root?.querySelectorAll<HTMLElement>('.cgt-td-tab').forEach(el => {
      el.classList.toggle('cgt-td-tab-active', el.dataset.tab === tab);
    });
    if (tab !== 'live') this.disconnectLive();
    if (tab === 'analytics') void this.renderAnalytics();
    else if (tab === 'assignments') void this.renderAssignments();
    else if (tab === 'logins') void this.renderLogins();
    else if (tab === 'debriefs') void this.renderDebriefs();
    else if (tab === 'live') void this.renderLive();
    else void this.renderStudentList();
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-td-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
    this.disconnectLive();
  }

  private buildShell(): HTMLElement {
    const root = document.createElement('div');
    root.id = PANEL_ID;
    root.innerHTML = `
      <div class="cgt-td-backdrop" data-close></div>
      <div class="cgt-td-card">
        <header>
          <div>
            <h2>Dashboard del profesor</h2>
            <p class="cgt-td-sub">Seguimiento de alumnos en tiempo real.</p>
          </div>
          <button type="button" class="cgt-td-close" data-close aria-label="Cerrar">×</button>
        </header>
        <nav class="cgt-td-tabs">
          <button type="button" class="cgt-td-tab ${this.activeTab === 'students' ? 'cgt-td-tab-active' : ''}" data-tab="students">Alumnos</button>
          <button type="button" class="cgt-td-tab ${this.activeTab === 'analytics' ? 'cgt-td-tab-active' : ''}" data-tab="analytics">Analytics</button>
          <button type="button" class="cgt-td-tab ${this.activeTab === 'assignments' ? 'cgt-td-tab-active' : ''}" data-tab="assignments">Asignaciones</button>
          <button type="button" class="cgt-td-tab ${this.activeTab === 'logins' ? 'cgt-td-tab-active' : ''}" data-tab="logins">Accesos</button>
          <button type="button" class="cgt-td-tab ${this.activeTab === 'debriefs' ? 'cgt-td-tab-active' : ''}" data-tab="debriefs">Debriefs IA</button>
          <button type="button" class="cgt-td-tab ${this.activeTab === 'live' ? 'cgt-td-tab-active' : ''}" data-tab="live">🔴 En vivo</button>
        </nav>
        <div class="cgt-td-body"></div>
      </div>
    `;
    root.querySelectorAll<HTMLElement>('[data-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    );
    root.querySelectorAll<HTMLButtonElement>('.cgt-td-tab').forEach(el => {
      el.addEventListener('click', () => {
        const tab = el.dataset.tab as Tab;
        if (tab) this.setTab(tab);
      });
    });
    return root;
  }

  /**
   * Tab "Asignaciones" — vista de grupo con completitud por caso.
   * Agrupa todas las asignaciones del profesor por case_slug y muestra
   * cuántos alumnos completaron vs cuántos pendientes/vencidos.
   */
  private async renderAssignments(): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    body.innerHTML = '<div class="cgt-td-loading">Cargando asignaciones…</div>';

    let rows: Assignment[];
    try {
      rows = await assignmentsService.listForTeacher();
    } catch (e) {
      body.innerHTML = `<div class="cgt-td-error">Error: ${this.escape(String(e))}</div>`;
      return;
    }

    if (rows.length === 0) {
      body.innerHTML = `
        <div class="cgt-td-empty">
          <p>Aún no asignaste casos.</p>
          <p style="font-size:11.5px;color:#6e6e73;margin-top:6px;">
            Abrí el panel <strong>📋 Asignar casos</strong> desde el sidebar para empezar.
          </p>
        </div>
      `;
      return;
    }

    // Agrupar por case_slug
    const groups = new Map<string, Assignment[]>();
    rows.forEach(r => {
      const list = groups.get(r.case_slug) ?? [];
      list.push(r);
      groups.set(r.case_slug, list);
    });

    const blocks: string[] = [];
    for (const [slug, list] of groups.entries()) {
      const done = list.filter(r => r.status === 'done').length;
      const overdue = list.filter(r => r.status === 'overdue').length;
      const pending = list.filter(r => r.status === 'pending').length;
      const total = list.length;
      const pct = Math.round((done / total) * 100);
      const scores = list.filter(r => r.last_score !== null).map(r => r.last_score as number);
      const avg = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

      const rowsHtml = list
        .sort((a, b) => Number(a.completed_at !== null) - Number(b.completed_at !== null))
        .map(r => {
          const color = r.status === 'done' ? '#30D158' : r.status === 'overdue' ? '#FF453A' : '#FF9F0A';
          const label = r.status === 'done' ? 'Completada' : r.status === 'overdue' ? 'Vencida' : 'Pendiente';
          const score = r.last_score !== null ? `${r.last_score}/100` : '—';
          const due = r.due_at ? new Date(r.due_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }) : '—';
          return `
            <tr>
              <td>${this.escape(r.student_name ?? '?')}</td>
              <td><span class="cgt-td-pill" style="background:${color}20;color:${color};border:0.5px solid ${color}60">${label}</span></td>
              <td class="cgt-td-mono">${score}</td>
              <td class="cgt-td-mono">${due}</td>
            </tr>
          `;
        }).join('');

      blocks.push(`
        <section class="cgt-td-assign-group">
          <header class="cgt-td-assign-head">
            <div>
              <h4>${this.escape(slug)}</h4>
              <small>${done}/${total} completadas · ${pending} pendientes · ${overdue} vencidas${avg !== null ? ` · prom ${avg}/100` : ''}</small>
            </div>
            <div class="cgt-td-assign-progress">
              <div style="width:${pct}%;background:#30D158"></div>
            </div>
            <strong class="cgt-td-mono">${pct}%</strong>
          </header>
          <table class="cgt-td-table">
            <thead><tr><th>Alumno</th><th>Estado</th><th>Score</th><th>Vence</th></tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </section>
      `);
    }

    body.innerHTML = `
      <div class="cgt-td-assign-wrap">
        ${blocks.join('')}
      </div>
    `;
  }

  /**
   * Tab "Accesos" — bitácora de logins (exitosos + fallidos).
   * Default muestra todos, con toggle para filtrar solo fallidos (útil para
   * detectar intentos de brute-force).
   */
  private loginFilterFailed = false;

  private async renderLogins(): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    body.innerHTML = '<div class="cgt-td-loading">Cargando registro de accesos…</div>';

    let rows: LoginEventRow[];
    try {
      const q = this.loginFilterFailed ? '?only_failed=true&limit=300' : '?limit=300';
      rows = await authService.request<LoginEventRow[]>(`/teacher/login-log${q}`);
    } catch (e) {
      body.innerHTML = `<div class="cgt-td-error">Error: ${this.escape(String(e))}</div>`;
      return;
    }

    const total = rows.length;
    const failed = rows.filter(r => !r.success).length;

    body.innerHTML = `
      <div class="cgt-td-login-toolbar">
        <div>
          <strong>${total}</strong> eventos · <span style="color:#FF6961">${failed} fallidos</span>
        </div>
        <label class="cgt-td-login-toggle">
          <input type="checkbox" data-only-failed ${this.loginFilterFailed ? 'checked' : ''} />
          Solo fallidos
        </label>
      </div>
      ${rows.length === 0 ? '<div class="cgt-td-empty">Sin registros.</div>' : `
        <table class="cgt-td-table cgt-td-login-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Usuario</th>
              <th>Rol</th>
              <th>IP</th>
              <th>Resultado</th>
              <th>Navegador</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(r => this.renderLoginRow(r)).join('')}
          </tbody>
        </table>
      `}
    `;
    body.querySelector<HTMLInputElement>('[data-only-failed]')?.addEventListener('change', (ev) => {
      this.loginFilterFailed = (ev.target as HTMLInputElement).checked;
      void this.renderLogins();
    });
  }

  private renderLoginRow(r: LoginEventRow): string {
    const when = new Date(r.logged_at);
    const whenStr = when.toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
    const ua = this.shortenUserAgent(r.user_agent ?? '');
    const statusColor = r.success ? '#30D158' : '#FF6961';
    const statusLabel = r.success ? '✓ Éxito' : `✗ ${r.error_reason ?? 'fallido'}`;
    const roleLabel = r.role ? this.escape(r.role) : '—';
    return `
      <tr>
        <td class="cgt-td-mono">${this.escape(whenStr)}</td>
        <td>${this.escape(r.email)}</td>
        <td>${roleLabel}</td>
        <td class="cgt-td-mono">${this.escape(r.ip ?? '—')}</td>
        <td><span class="cgt-td-pill" style="background:${statusColor}20;color:${statusColor};border:0.5px solid ${statusColor}60">${this.escape(statusLabel)}</span></td>
        <td class="cgt-td-ua">${this.escape(ua)}</td>
      </tr>
    `;
  }

  private debriefMinScore: number | null = null;

  /** Sparkline SVG de scores 0-100. Color según score más reciente. */
  private renderSparkline(values: number[]): string {
    if (values.length === 0) return '';
    const W = 280, H = 60, pad = 4;
    const stepX = (W - pad * 2) / Math.max(1, values.length - 1);
    const points = values.map((v, i) => {
      const x = pad + i * stepX;
      const y = H - pad - (v / 100) * (H - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    const last = values[values.length - 1];
    const color = last >= 80 ? '#30D158' : last >= 60 ? '#FFD60A' : '#FF6961';
    // Línea base 60 pts (umbral aprobación): línea horizontal punteada
    const yBase = H - pad - (60 / 100) * (H - pad * 2);
    return `
      <svg viewBox="0 0 ${W} ${H}" class="cgt-td-sparkline" preserveAspectRatio="none">
        <line x1="${pad}" y1="${yBase}" x2="${W - pad}" y2="${yBase}"
          stroke="rgba(255,255,255,0.2)" stroke-dasharray="2,3" stroke-width="0.5"/>
        <polyline fill="none" stroke="${color}" stroke-width="1.5" points="${points}"/>
        ${values.map((v, i) => {
          const x = pad + i * stepX;
          const y = H - pad - (v / 100) * (H - pad * 2);
          return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${color}"/>`;
        }).join('')}
      </svg>
    `;
  }

  private async renderDebriefs(): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    body.innerHTML = '<div class="cgt-td-loading">Cargando debriefs IA…</div>';

    let rows: DebriefRow[];
    try {
      const q = this.debriefMinScore !== null
        ? `?limit=200&max_score=${this.debriefMinScore}`
        : '?limit=200';
      rows = await authService.request<DebriefRow[]>(`/teacher/debriefs${q}`);
    } catch (e) {
      body.innerHTML = `<div class="cgt-td-error">Error: ${this.escape(String(e))}</div>`;
      return;
    }

    const total = rows.length;
    const avg = total ? Math.round(rows.reduce((s, r) => s + r.score_0_100, 0) / total) : 0;
    const lowCount = rows.filter(r => r.score_0_100 < 60).length;

    body.innerHTML = `
      <div class="cgt-td-login-toolbar">
        <div>
          <strong>${total}</strong> debriefs · score promedio <strong>${avg}/100</strong>
          ${lowCount ? ` · <span style="color:#FF6961">${lowCount} bajo 60</span>` : ''}
        </div>
        <label class="cgt-td-login-toggle">
          <input type="checkbox" data-only-low ${this.debriefMinScore !== null ? 'checked' : ''} />
          Solo score &lt; 60
        </label>
      </div>
      ${rows.length === 0 ? '<div class="cgt-td-empty">Sin debriefs aún. Los alumnos generan debriefs al cerrar el monitor.</div>' : `
        <div class="cgt-td-debriefs-list">
          ${rows.map(r => this.renderDebriefRow(r)).join('')}
        </div>
      `}
    `;
    body.querySelector<HTMLInputElement>('[data-only-low]')?.addEventListener('change', (ev) => {
      this.debriefMinScore = (ev.target as HTMLInputElement).checked ? 59 : null;
      void this.renderDebriefs();
    });
    body.querySelectorAll<HTMLElement>('[data-debrief-toggle]').forEach(el => {
      el.addEventListener('click', () => {
        const id = el.dataset.debriefToggle;
        const detail = body.querySelector<HTMLElement>(`[data-debrief-detail="${id}"]`);
        if (detail) detail.classList.toggle('cgt-td-hidden');
      });
    });
  }

  private renderDebriefRow(r: DebriefRow): string {
    const when = new Date(r.created_at);
    const whenStr = when.toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
    const scoreColor = r.score_0_100 >= 80 ? '#30D158'
      : r.score_0_100 >= 60 ? '#FFD60A' : '#FF6961';
    const dx = r.diagnosis ? this.escape(r.diagnosis) : '—';
    const cc = r.chief_complaint ? this.escape(r.chief_complaint) : '';
    return `
      <div class="cgt-td-debrief-card">
        <div class="cgt-td-debrief-head" data-debrief-toggle="${r.id}">
          <div class="cgt-td-debrief-score" style="background:${scoreColor}20;color:${scoreColor};border-color:${scoreColor}60">
            <strong>${r.score_0_100}</strong>/100
          </div>
          <div class="cgt-td-debrief-meta">
            <div class="cgt-td-debrief-student">${this.escape(r.student_name)} · ${this.escape(r.student_email)}</div>
            <div class="cgt-td-debrief-case">${dx}${cc ? ` · "${cc}"` : ''}</div>
            <div class="cgt-td-debrief-when">${this.escape(whenStr)} · ${r.duration_min.toFixed(1)} min</div>
          </div>
          <span class="cgt-td-debrief-chevron">▾</span>
        </div>
        <div class="cgt-td-debrief-detail cgt-td-hidden" data-debrief-detail="${r.id}">
          <div class="cgt-td-debrief-section">
            <h5>Resumen</h5>
            <p>${this.escape(r.summary)}</p>
          </div>
          ${r.strengths.length ? `<div class="cgt-td-debrief-section"><h5 style="color:#30D158">✅ Fortalezas</h5><ul>${r.strengths.map(s => `<li>${this.escape(s)}</li>`).join('')}</ul></div>` : ''}
          ${r.gaps.length ? `<div class="cgt-td-debrief-section"><h5 style="color:#FFD60A">⚠️ Omisiones</h5><ul>${r.gaps.map(s => `<li>${this.escape(s)}</li>`).join('')}</ul></div>` : ''}
          ${r.pearls.length ? `<div class="cgt-td-debrief-section"><h5 style="color:#2997FF">💡 Pearls</h5><ul>${r.pearls.map(s => `<li>${this.escape(s)}</li>`).join('')}</ul></div>` : ''}
          ${r.next_case ? `<div class="cgt-td-debrief-section"><h5>🎯 Próximo caso sugerido</h5><p>${this.escape(r.next_case)}</p></div>` : ''}
        </div>
      </div>
    `;
  }

  // ──────────────────── Vista en vivo (Sprint 5) ────────────────────
  /** Buffer por alumno: últimos N eventos + snapshot de vitales del feed live. */
  private liveByStudent = new Map<number, {
    name: string;
    events: Array<{ at: number; kind: string; label: string }>;
    vitals?: Record<string, number | null>;
    vitalsAt?: number;
    /** Marca temporal: cuándo terminó el caso (para fade-out o "ended"). */
    endedAt?: number;
  }>();
  private liveUnsubscribe: (() => void) | null = null;
  private liveConnected = false;
  private liveRefreshTimer: number | null = null;

  private async renderLive(): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    if (!this.liveConnected) {
      liveSession.connect(authService.currentUser?.live_room ?? 'global-live');
      this.liveConnected = true;
      this.liveUnsubscribe = liveSession.onEvent((ev) => this.onLiveEvent(ev as never));
      // Auto-refresh cada 5s mientras live esté abierto: actualiza "hace Xs"
      // y expira cards de alumnos que cerraron sesión hace >2 min.
      this.liveRefreshTimer = window.setInterval(() => {
        if (this.activeTab !== 'live') return;
        this.expireOldLiveStudents();
        this.renderLiveBody();
      }, 5000);
    }
    this.renderLiveBody();
  }

  private disconnectLive(): void {
    if (!this.liveConnected) return;
    this.liveUnsubscribe?.();
    this.liveUnsubscribe = null;
    liveSession.disconnect();
    this.liveConnected = false;
    this.liveByStudent.clear();
    if (this.liveRefreshTimer !== null) window.clearInterval(this.liveRefreshTimer);
    this.liveRefreshTimer = null;
  }

  /** Remueve cards de alumnos que cerraron caso hace >2 min (cleanup). */
  private expireOldLiveStudents(): void {
    const cutoff = Date.now() - 2 * 60_000;
    for (const [uid, data] of this.liveByStudent.entries()) {
      if (data.endedAt && data.endedAt < cutoff) this.liveByStudent.delete(uid);
    }
  }

  private onLiveEvent(ev: { type: string; from_user_id?: number; from_name?: string; from_role?: string; user_id?: number; [k: string]: unknown }): void {
    // system.peerLeft: el alumno desconectó (cerró pestaña, se cayó la red).
    // Si el caso terminó "limpio" ya tendrá endedAt; si no, lo marcamos acá.
    if (ev.type === 'system.peerLeft') {
      const lid = ev.user_id;
      if (lid !== undefined) {
        const e = this.liveByStudent.get(lid);
        if (e && !e.endedAt) {
          e.endedAt = Date.now();
          e.events.unshift({ at: Date.now(), kind: 'disconnect', label: '🔌 Desconectado' });
          this.liveByStudent.set(lid, e);
          if (this.activeTab === 'live') this.renderLiveBody();
        }
      }
      return;
    }
    if (!ev.type.startsWith('student.')) return;
    const uid = ev.from_user_id;
    if (uid === undefined) return;
    const entry = this.liveByStudent.get(uid) ?? { name: ev.from_name ?? `Alumno ${uid}`, events: [] };
    // student.vitals no se loggea como evento, actualiza el snapshot del card.
    if (ev.type === 'student.vitals') {
      const v = (ev as { vitals?: Record<string, number | null> }).vitals;
      if (v) { entry.vitals = v; entry.vitalsAt = Date.now(); }
      this.liveByStudent.set(uid, entry);
      if (this.activeTab === 'live') this.renderLiveBody();
      return;
    }
    let label = ev.type.replace('student.', '');
    if (ev.type === 'student.action') label = `🎯 ${(ev as { label?: string }).label ?? ''}`;
    else if (ev.type === 'student.drug') label = `💊 ${(ev as { name?: string }).name ?? ''} ${((ev as { dose?: string }).dose ?? '')}`;
    else if (ev.type === 'student.gesture') label = `🫸 ${(ev as { gesture?: string }).gesture ?? ''}`;
    else if (ev.type === 'student.voice') {
      const v = ev as { role?: string; text?: string };
      label = `${v.role === 'student' ? '🩺' : '🗣️'} "${(v.text ?? '').slice(0, 80)}"`;
    } else if (ev.type === 'student.caseStart') {
      const c = ev as { diagnosis?: string | null; chiefComplaint?: string | null };
      label = `▶️ Caso: ${c.chiefComplaint ?? c.diagnosis ?? 'sin info'}`;
    } else if (ev.type === 'student.caseEnd') {
      label = `⏹️ Caso cerrado`;
      entry.endedAt = Date.now();
    } else if (ev.type === 'student.caseStart') {
      // Si re-abre un caso después de uno terminado, limpiamos el flag.
      entry.endedAt = undefined;
    }
    entry.events.unshift({ at: Date.now(), kind: ev.type, label });
    if (entry.events.length > 25) entry.events.pop();
    this.liveByStudent.set(uid, entry);
    // Re-render solo si el tab live sigue activo
    if (this.activeTab === 'live') this.renderLiveBody();
  }

  private renderLiveVitals(vitals: Record<string, number | null>, at?: number): string {
    const fmt = (v: number | null | undefined, dp = 0): string =>
      v === null || v === undefined ? '—' : v.toFixed(dp);
    const ageSec = at ? Math.round((Date.now() - at) / 1000) : null;
    const stale = ageSec !== null && ageSec > 30;
    return `
      <div class="cgt-td-live-vitals${stale ? ' cgt-td-live-vitals-stale' : ''}">
        <div class="cgt-td-vital-cell" title="Frecuencia cardíaca">
          <span class="cgt-td-vital-label">HR</span>
          <span class="cgt-td-vital-value" style="color:#30D158">${fmt(vitals.hr)}</span>
        </div>
        <div class="cgt-td-vital-cell" title="SpO₂">
          <span class="cgt-td-vital-label">SpO₂</span>
          <span class="cgt-td-vital-value" style="color:#2997FF">${fmt(vitals.spo2)}</span>
        </div>
        <div class="cgt-td-vital-cell" title="Presión arterial">
          <span class="cgt-td-vital-label">PA</span>
          <span class="cgt-td-vital-value" style="color:#FF453A">${fmt(vitals.sbp)}/${fmt(vitals.dbp)}</span>
        </div>
        <div class="cgt-td-vital-cell" title="Frecuencia respiratoria">
          <span class="cgt-td-vital-label">RR</span>
          <span class="cgt-td-vital-value" style="color:#FFD60A">${fmt(vitals.rr)}</span>
        </div>
        ${ageSec !== null ? `<span class="cgt-td-vital-age">hace ${ageSec}s</span>` : ''}
      </div>
    `;
  }

  private renderLiveBody(): void {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    const students = [...this.liveByStudent.entries()];
    body.innerHTML = `
      <div class="cgt-td-login-toolbar">
        <div>
          <span class="cgt-td-live-dot"></span>
          ${this.liveConnected ? 'Conectado al room' : 'Conectando…'} ·
          <strong>${students.length}</strong> alumno(s) activo(s)
        </div>
      </div>
      ${students.length === 0 ? `
        <div class="cgt-td-empty">Esperando alumnos. Cuando un alumno abra el monitor, su feed aparecerá acá en tiempo real.</div>
      ` : `
        <div class="cgt-td-live-grid">
          ${students.map(([uid, data]) => `
            <div class="cgt-td-live-card${data.endedAt ? ' cgt-td-live-card-ended' : ''}">
              <header>
                <strong>${this.escape(data.name)}</strong>
                <span class="cgt-td-mono">${data.endedAt ? 'cerrado' : `id ${uid}`}</span>
              </header>
              ${data.vitals ? this.renderLiveVitals(data.vitals, data.vitalsAt) : ''}
              <ul>
                ${data.events.slice(0, 12).map(e => {
                  const t = new Date(e.at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  return `<li><code>${t}</code> ${this.escape(e.label)}</li>`;
                }).join('')}
              </ul>
            </div>
          `).join('')}
        </div>
      `}
    `;
  }

  private shortenUserAgent(ua: string): string {
    if (!ua) return '—';
    // Heurística simple: sacar browser + OS.
    const m = ua.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/);
    const browser = m?.[1] ?? '?';
    const os = ua.includes('Macintosh') ? 'macOS'
      : ua.includes('Windows') ? 'Windows'
      : ua.includes('Linux') ? 'Linux'
      : ua.includes('Android') ? 'Android'
      : ua.includes('iPhone') ? 'iPhone'
      : '?';
    return `${browser} · ${os}`;
  }

  private async renderAnalytics(): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    body.innerHTML = '<div class="cgt-td-loading">Cargando analytics…</div>';

    let data: TeacherAnalyticsResponse;
    try {
      data = await authService.request<TeacherAnalyticsResponse>('/teacher/analytics');
    } catch (error) {
      body.innerHTML = `<div class="cgt-td-error">No se pudo cargar: ${this.escape(String(error))}</div>`;
      return;
    }

    const globalAccPct = Math.round(data.global_accuracy * 100);
    const maxDaily = Math.max(1, ...data.daily_activity.map(d => d.attempts));
    const maxBucket = Math.max(1, ...data.accuracy_distribution.map(b => b.students));

    body.innerHTML = `
      <div class="cgt-td-kpis">
        ${this.kpi('Alumnos', data.total_students)}
        ${this.kpi('Activos 7d', data.active_last_7_days)}
        ${this.kpi('Intentos', data.total_attempts)}
        ${this.kpi('Precisión global', `${globalAccPct}%`)}
        ${this.kpi('XP promedio', Math.round(data.avg_xp))}
      </div>

      <h4 class="cgt-td-section">Casos más intentados</h4>
      ${data.top_cases.length === 0 ? '<div class="cgt-td-empty">Sin intentos aún.</div>' : `
        <table class="cgt-td-table">
          <thead><tr><th>Caso</th><th>Intentos</th><th>Precisión</th><th>Duración prom.</th></tr></thead>
          <tbody>
            ${data.top_cases.map(c => `
              <tr>
                <td>${this.escape(c.case_id)}</td>
                <td class="cgt-td-mono">${c.attempts}</td>
                <td>${this.renderAccuracyBar(c.accuracy)}</td>
                <td class="cgt-td-mono">${Math.round(c.avg_duration_seconds)}s</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `}

      <h4 class="cgt-td-section">Actividad últimos 30 días</h4>
      ${data.daily_activity.length === 0 ? '<div class="cgt-td-empty">Sin actividad reciente.</div>' : `
        <div class="cgt-td-daily">
          ${data.daily_activity.map(d => {
            const h = Math.max(4, Math.round((d.attempts / maxDaily) * 80));
            const correctH = d.attempts ? Math.round((d.correct / d.attempts) * h) : 0;
            return `
              <div class="cgt-td-bar-col" title="${d.date}: ${d.correct}/${d.attempts}">
                <div class="cgt-td-bar-wrap" style="height:80px">
                  <div class="cgt-td-bar" style="height:${h}px"></div>
                  <div class="cgt-td-bar-correct" style="height:${correctH}px"></div>
                </div>
                <span>${d.date.slice(5)}</span>
              </div>
            `;
          }).join('')}
        </div>
        <div class="cgt-td-legend">
          <span><i style="background:rgba(41,151,255,0.3)"></i>Intentos</span>
          <span><i style="background:#30D158"></i>Correctos</span>
        </div>
      `}

      <h4 class="cgt-td-section">Distribución de precisión por alumno</h4>
      <div class="cgt-td-buckets">
        ${data.accuracy_distribution.map(b => {
          const w = Math.round((b.students / maxBucket) * 100);
          const color = b.label === '75-100%' ? '#30D158' : b.label === '50-75%' ? '#FF9F0A' : b.label === '25-50%' ? '#FF7F50' : '#FF453A';
          return `
            <div class="cgt-td-bucket">
              <span class="cgt-td-bucket-label">${b.label}</span>
              <div class="cgt-td-bucket-bar"><div style="width:${w}%;background:${color}"></div></div>
              <span class="cgt-td-mono">${b.students}</span>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  private kpi(label: string, value: string | number): string {
    return `<div class="cgt-td-kpi"><span>${this.escape(label)}</span><strong>${value}</strong></div>`;
  }

  private async renderStudentList(): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    body.innerHTML = '<div class="cgt-td-loading">Cargando alumnos…</div>';

    let students: StudentSummary[];
    try {
      students = await authService.request<StudentSummary[]>(
        `/teacher/students?order_by=${this.orderBy}`
      );
    } catch (error) {
      body.innerHTML = `<div class="cgt-td-error">No se pudo cargar: ${this.escape(String(error))}</div>`;
      return;
    }

    if (students.length === 0) {
      body.innerHTML = '<div class="cgt-td-empty">Aún no hay alumnos registrados.</div>';
      return;
    }

    body.innerHTML = `
      <div class="cgt-td-toolbar">
        <span>Ordenar por:</span>
        <button type="button" class="cgt-td-order${this.orderBy === 'xp' ? ' active' : ''}" data-order="xp">XP</button>
        <button type="button" class="cgt-td-order${this.orderBy === 'last_activity' ? ' active' : ''}" data-order="last_activity">Última actividad</button>
        <button type="button" class="cgt-td-order${this.orderBy === 'name' ? ' active' : ''}" data-order="name">Nombre</button>
      </div>
      <table class="cgt-td-table">
        <thead>
          <tr>
            <th>Alumno</th><th>Nivel</th><th>XP</th><th>Casos</th><th>Precisión</th><th>Última actividad</th><th>Mi clase</th>
          </tr>
        </thead>
        <tbody>
          ${students.map(s => {
            const me = authService.currentUser?.id;
            const mine = s.teacher_id === me;
            return `
              <tr data-student="${s.id}">
                <td>
                  <div class="cgt-td-name">${this.escape(s.display_name)}</div>
                  <div class="cgt-td-email">${this.escape(s.email)}</div>
                </td>
                <td><span class="cgt-td-level">L${s.level}</span></td>
                <td class="cgt-td-mono">${s.xp}</td>
                <td class="cgt-td-mono">${s.cases_correct}/${s.cases_completed}</td>
                <td>${this.renderAccuracyBar(s.accuracy)}</td>
                <td class="cgt-td-time">${this.formatRelative(s.last_activity_at)}</td>
                <td>
                  <button type="button" class="cgt-td-assign-btn ${mine ? 'is-mine' : ''}"
                    data-assign="${s.id}" data-mine="${mine ? '1' : '0'}"
                    title="${mine ? 'Quitar de mi clase' : 'Agregar a mi clase'}">
                    ${mine ? '✓' : '+'}
                  </button>
                </td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    `;

    body.querySelectorAll<HTMLButtonElement>('.cgt-td-order').forEach(btn => {
      btn.addEventListener('click', () => {
        this.orderBy = (btn.dataset.order ?? 'xp') as OrderBy;
        void this.renderStudentList();
      });
    });

    body.querySelectorAll<HTMLTableRowElement>('tr[data-student]').forEach(row => {
      row.addEventListener('click', (ev) => {
        // No abrir detalle si fue click en el botón de asignar
        if ((ev.target as HTMLElement).closest('.cgt-td-assign-btn')) return;
        const id = Number(row.dataset.student);
        if (Number.isFinite(id)) void this.renderStudentDetail(id, students.find(s => s.id === id));
      });
    });
    body.querySelectorAll<HTMLButtonElement>('.cgt-td-assign-btn').forEach(btn => {
      btn.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        const sid = Number(btn.dataset.assign);
        const mine = btn.dataset.mine === '1';
        const teacher_id = mine ? null : authService.currentUser?.id ?? null;
        try {
          await authService.request('/teacher/assign-student', {
            method: 'POST',
            body: JSON.stringify({ student_id: sid, teacher_id }),
          });
          await this.renderStudentList();
        } catch (e) {
          console.warn('[TeacherDashboard] assign error:', e);
        }
      });
    });
  }

  private async renderStudentDetail(id: number, summary?: StudentSummary): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('.cgt-td-body');
    if (!body) return;
    body.innerHTML = '<div class="cgt-td-loading">Cargando detalle…</div>';

    try {
      const [progress, attempts, debriefs] = await Promise.all([
        authService.request<ProgressRow>(`/teacher/students/${id}/progress`).catch(() => null),
        authService.request<AttemptRow[]>(`/teacher/students/${id}/attempts?limit=50`),
        authService.request<DebriefRow[]>(`/teacher/debriefs?student_id=${id}&limit=20`).catch(() => [] as DebriefRow[]),
      ]);

      // Sparkline IA-score: últimos N debriefs en orden cronológico (más
      // viejo a la izquierda). Permite ver tendencia: ¿mejora con la práctica?
      const scoreSeries = [...debriefs].reverse().map(d => d.score_0_100);
      const avgScore = scoreSeries.length
        ? Math.round(scoreSeries.reduce((s, v) => s + v, 0) / scoreSeries.length)
        : null;

      body.innerHTML = `
        <button type="button" class="cgt-td-back" data-back>← Volver</button>
        <div class="cgt-td-detail-header">
          <h3>${this.escape(summary?.display_name ?? `Alumno #${id}`)}</h3>
          <p>${this.escape(summary?.email ?? '')}</p>
        </div>
        ${scoreSeries.length >= 2 ? `
          <div class="cgt-td-sparkline-wrap">
            <div class="cgt-td-sparkline-head">
              <span>Tendencia score IA · últimos ${scoreSeries.length}</span>
              ${avgScore !== null ? `<strong>promedio ${avgScore}/100</strong>` : ''}
            </div>
            ${this.renderSparkline(scoreSeries)}
          </div>
        ` : ''}
        ${progress ? `
          <div class="cgt-td-stats">
            <div><span>Nivel</span><strong>${progress.level}</strong></div>
            <div><span>XP</span><strong>${progress.xp}</strong></div>
            <div><span>Casos</span><strong>${progress.cases_correct}/${progress.cases_completed}</strong></div>
            <div><span>Racha actual</span><strong>${progress.current_streak}</strong></div>
            <div><span>Mejor racha</span><strong>${progress.longest_streak}</strong></div>
          </div>
        ` : '<div class="cgt-td-empty">Sin progreso registrado todavía.</div>'}
        <h4 class="cgt-td-section">Últimos intentos</h4>
        ${attempts.length === 0 ? '<div class="cgt-td-empty">Sin intentos.</div>' : `
          <table class="cgt-td-table">
            <thead><tr><th>Caso</th><th>Dx</th><th>Triage</th><th>Score</th><th>Duración</th><th>Fecha</th></tr></thead>
            <tbody>
              ${attempts.map(a => `
                <tr>
                  <td>${this.escape(a.case_id)}</td>
                  <td>${a.diagnosis_correct ? '✅' : '❌'}</td>
                  <td>${a.triage_correct ? '✅' : '❌'}</td>
                  <td class="cgt-td-mono">${a.score}</td>
                  <td class="cgt-td-mono">${Math.round(a.duration_seconds)}s</td>
                  <td class="cgt-td-time">${this.formatRelative(a.completed_at ?? a.started_at)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `}
      `;

      body.querySelector('[data-back]')?.addEventListener('click', () => this.renderStudentList());
    } catch (error) {
      body.innerHTML = `<div class="cgt-td-error">No se pudo cargar el detalle: ${this.escape(String(error))}</div>`;
    }
  }

  private renderAccuracyBar(acc: number): string {
    const pct = Math.round(acc * 100);
    const color = acc >= 0.8 ? '#30D158' : acc >= 0.5 ? '#FF9F0A' : '#FF453A';
    return `
      <div class="cgt-td-acc">
        <div class="cgt-td-acc-bar"><div style="width:${pct}%;background:${color}"></div></div>
        <span class="cgt-td-mono">${pct}%</span>
      </div>
    `;
  }

  private formatRelative(iso: string | null): string {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.round(diff / 60000);
    if (min < 1) return 'ahora';
    if (min < 60) return `hace ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.round(h / 24);
    return `hace ${d} d`;
  }

  private escape(text: string): string {
    return text.replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c] ?? c));
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; inset: 0; z-index: 14000;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        opacity: 0; transition: opacity 0.2s ease;
      }
      #${PANEL_ID}.cgt-td-visible { opacity: 1; }
      #${PANEL_ID} .cgt-td-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.55); backdrop-filter: blur(4px); }
      #${PANEL_ID} .cgt-td-card {
        position: relative; max-width: 900px; width: 94vw; max-height: 86vh;
        margin: 7vh auto; background: #0a0a0a;
        border: 0.5px solid rgba(255,255,255,0.1); border-radius: 18px;
        display: flex; flex-direction: column; overflow: hidden;
        box-shadow: 0 20px 60px rgba(0,0,0,0.5);
      }
      #${PANEL_ID} header {
        display: flex; align-items: start; justify-content: space-between;
        padding: 20px 24px; border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} header h2 { font-size: 20px; font-weight: 700; }
      #${PANEL_ID} .cgt-td-sub { font-size: 12px; color: #a1a1a6; margin-top: 4px; }
      #${PANEL_ID} .cgt-td-close {
        width: 28px; height: 28px; border-radius: 50%;
        background: rgba(255,255,255,0.06); border: none; cursor: pointer;
        color: #a1a1a6; font-size: 18px;
      }
      #${PANEL_ID} .cgt-td-body { padding: 18px 24px 24px; overflow-y: auto; flex: 1; }
      #${PANEL_ID} .cgt-td-loading, #${PANEL_ID} .cgt-td-empty, #${PANEL_ID} .cgt-td-error {
        padding: 30px; text-align: center; color: #a1a1a6;
      }
      #${PANEL_ID} .cgt-td-error { color: #FF6961; }

      #${PANEL_ID} .cgt-td-toolbar {
        display: flex; align-items: center; gap: 8px;
        margin-bottom: 14px; font-size: 12px; color: #a1a1a6;
      }
      #${PANEL_ID} .cgt-td-order {
        background: rgba(255,255,255,0.04); border: 0.5px solid transparent;
        color: #a1a1a6; padding: 4px 10px; border-radius: 6px;
        font-size: 11px; cursor: pointer;
      }
      #${PANEL_ID} .cgt-td-order.active { background: rgba(41,151,255,0.15); color: #2997FF; border-color: rgba(41,151,255,0.3); }

      #${PANEL_ID} .cgt-td-table { width: 100%; border-collapse: collapse; font-size: 13px; }
      #${PANEL_ID} .cgt-td-table th {
        text-align: left; padding: 10px 12px; color: #6e6e73;
        font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;
        border-bottom: 0.5px solid rgba(255,255,255,0.08);
      }
      #${PANEL_ID} .cgt-td-table td {
        padding: 12px; border-bottom: 0.5px solid rgba(255,255,255,0.04);
      }
      #${PANEL_ID} .cgt-td-table tbody tr { cursor: pointer; transition: background 0.15s; }
      #${PANEL_ID} .cgt-td-table tbody tr:hover { background: rgba(255,255,255,0.03); }

      #${PANEL_ID} .cgt-td-name { font-weight: 600; }
      #${PANEL_ID} .cgt-td-email { font-size: 11px; color: #6e6e73; margin-top: 2px; }
      #${PANEL_ID} .cgt-td-level {
        display: inline-block; padding: 3px 8px; border-radius: 5px;
        background: rgba(191,90,242,0.15); color: #BF5AF2;
        font-size: 11px; font-weight: 700; font-family: 'JetBrains Mono', monospace;
      }
      #${PANEL_ID} .cgt-td-mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; }
      #${PANEL_ID} .cgt-td-time { color: #a1a1a6; font-size: 11px; }

      #${PANEL_ID} .cgt-td-acc { display: flex; align-items: center; gap: 8px; }
      #${PANEL_ID} .cgt-td-acc-bar {
        flex: 1; height: 4px; background: rgba(255,255,255,0.06);
        border-radius: 2px; overflow: hidden; min-width: 60px;
      }
      #${PANEL_ID} .cgt-td-acc-bar div { height: 100%; transition: width 0.3s; }

      /* Tab Asignaciones */
      #${PANEL_ID} .cgt-td-assign-wrap {
        display: flex; flex-direction: column; gap: 16px;
      }
      #${PANEL_ID} .cgt-td-assign-group {
        background: rgba(255,255,255,0.02);
        border: 0.5px solid rgba(255,255,255,0.06);
        border-radius: 12px; padding: 14px 16px;
      }
      #${PANEL_ID} .cgt-td-assign-head {
        display: grid; grid-template-columns: 1fr 140px 40px;
        align-items: center; gap: 14px; margin-bottom: 10px;
      }
      #${PANEL_ID} .cgt-td-assign-head h4 {
        font-size: 13px; font-weight: 700; color: #f5f5f7;
      }
      #${PANEL_ID} .cgt-td-assign-head small {
        display: block; font-size: 11px; color: #8e8e93; margin-top: 2px;
      }
      #${PANEL_ID} .cgt-td-assign-progress {
        height: 6px; background: rgba(255,255,255,0.06);
        border-radius: 3px; overflow: hidden;
      }
      #${PANEL_ID} .cgt-td-assign-progress div {
        height: 100%; transition: width 0.3s;
      }
      #${PANEL_ID} .cgt-td-pill {
        display: inline-block; padding: 2px 8px;
        border-radius: 10px; font-size: 10.5px; font-weight: 600;
      }

      /* Tab Accesos */
      #${PANEL_ID} .cgt-td-login-toolbar {
        display: flex; justify-content: space-between; align-items: center;
        padding: 10px 12px; margin-bottom: 10px;
        background: rgba(255,255,255,0.02);
        border: 0.5px solid rgba(255,255,255,0.06);
        border-radius: 8px; font-size: 12px; color: #a1a1a6;
      }
      #${PANEL_ID} .cgt-td-login-toolbar strong { color: #f5f5f7; }
      #${PANEL_ID} .cgt-td-login-toggle {
        display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
        font-size: 11.5px;
      }
      #${PANEL_ID} .cgt-td-login-toggle input { accent-color: #2997FF; }
      #${PANEL_ID} .cgt-td-login-table td { font-size: 11.5px; padding: 6px 8px; }
      #${PANEL_ID} .cgt-td-ua { font-size: 10.5px; color: #8e8e93; }

      #${PANEL_ID} .cgt-td-back {
        background: none; border: none; color: #2997FF;
        font-size: 12px; cursor: pointer; margin-bottom: 12px; padding: 0;
      }
      #${PANEL_ID} .cgt-td-detail-header h3 { font-size: 18px; font-weight: 700; }
      #${PANEL_ID} .cgt-td-detail-header p { color: #6e6e73; font-size: 12px; margin-top: 2px; }
      #${PANEL_ID} .cgt-td-stats {
        display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px;
        margin: 18px 0;
      }
      #${PANEL_ID} .cgt-td-stats > div {
        background: rgba(255,255,255,0.03); border-radius: 10px; padding: 12px;
      }
      #${PANEL_ID} .cgt-td-stats span {
        display: block; font-size: 10px; color: #6e6e73;
        text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;
      }
      #${PANEL_ID} .cgt-td-stats strong {
        font-family: 'JetBrains Mono', monospace; font-size: 18px; font-weight: 700; color: #fff;
      }
      #${PANEL_ID} .cgt-td-section {
        margin-top: 22px; margin-bottom: 10px; font-size: 13px; font-weight: 600;
      }

      #${PANEL_ID} .cgt-td-tabs {
        display: flex; gap: 4px; padding: 0 22px;
        border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} .cgt-td-tab {
        background: transparent; border: none; color: #a1a1a6;
        font-size: 12px; font-weight: 500; padding: 10px 14px;
        border-bottom: 2px solid transparent; cursor: pointer;
        font-family: inherit;
      }
      #${PANEL_ID} .cgt-td-tab:hover { color: #fff; }
      #${PANEL_ID} .cgt-td-tab-active { color: #2997FF; border-bottom-color: #2997FF; }

      #${PANEL_ID} .cgt-td-kpis {
        display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px;
        margin-bottom: 10px;
      }
      #${PANEL_ID} .cgt-td-kpi {
        background: rgba(255,255,255,0.03); border-radius: 10px; padding: 12px;
      }
      #${PANEL_ID} .cgt-td-kpi span {
        display: block; font-size: 10px; color: #6e6e73;
        text-transform: uppercase; letter-spacing: 0.5px;
      }
      #${PANEL_ID} .cgt-td-kpi strong {
        display: block; font-family: 'JetBrains Mono', monospace;
        font-size: 20px; font-weight: 700; color: #fff; margin-top: 3px;
      }

      #${PANEL_ID} .cgt-td-daily {
        display: flex; align-items: end; gap: 2px; padding: 10px 0;
        overflow-x: auto;
      }
      #${PANEL_ID} .cgt-td-bar-col {
        display: flex; flex-direction: column; align-items: center;
        flex: 1 0 18px; min-width: 18px;
      }
      #${PANEL_ID} .cgt-td-bar-wrap {
        width: 100%; position: relative;
        display: flex; align-items: end; justify-content: center;
      }
      #${PANEL_ID} .cgt-td-bar {
        width: 70%; background: rgba(41,151,255,0.3); border-radius: 2px 2px 0 0;
        position: absolute; bottom: 0;
      }
      #${PANEL_ID} .cgt-td-bar-correct {
        width: 70%; background: #30D158; border-radius: 2px 2px 0 0;
        position: absolute; bottom: 0;
      }
      #${PANEL_ID} .cgt-td-bar-col span {
        font-size: 9px; color: #6e6e73; margin-top: 4px;
        font-family: 'JetBrains Mono', monospace;
        transform: rotate(-45deg); white-space: nowrap;
        transform-origin: left top;
      }
      #${PANEL_ID} .cgt-td-legend {
        display: flex; gap: 14px; padding: 20px 0 4px;
        font-size: 10px; color: #a1a1a6;
      }
      #${PANEL_ID} .cgt-td-legend i {
        display: inline-block; width: 10px; height: 10px;
        border-radius: 2px; margin-right: 6px; vertical-align: middle;
      }

      #${PANEL_ID} .cgt-td-buckets { display: flex; flex-direction: column; gap: 8px; }
      #${PANEL_ID} .cgt-td-bucket {
        display: grid; grid-template-columns: 80px 1fr 32px;
        align-items: center; gap: 10px;
      }
      #${PANEL_ID} .cgt-td-bucket-label {
        font-size: 11px; color: #a1a1a6;
        font-family: 'JetBrains Mono', monospace;
      }
      #${PANEL_ID} .cgt-td-bucket-bar {
        height: 10px; background: rgba(255,255,255,0.05);
        border-radius: 5px; overflow: hidden;
      }
      #${PANEL_ID} .cgt-td-bucket-bar div { height: 100%; transition: width 0.3s; }

      /* Debriefs IA */
      #${PANEL_ID} .cgt-td-debriefs-list { display: flex; flex-direction: column; gap: 10px; padding-top: 10px; }
      #${PANEL_ID} .cgt-td-debrief-card {
        background: rgba(255,255,255,0.03); border: 0.5px solid rgba(255,255,255,0.06);
        border-radius: 10px; overflow: hidden;
      }
      #${PANEL_ID} .cgt-td-debrief-head {
        display: grid; grid-template-columns: 90px 1fr 24px; gap: 14px;
        align-items: center; padding: 12px 14px; cursor: pointer;
        transition: background 120ms;
      }
      #${PANEL_ID} .cgt-td-debrief-head:hover { background: rgba(255,255,255,0.04); }
      #${PANEL_ID} .cgt-td-debrief-score {
        text-align: center; padding: 10px 6px; border-radius: 8px;
        border: 0.5px solid; font-size: 10px;
      }
      #${PANEL_ID} .cgt-td-debrief-score strong { font-size: 18px; display: block; line-height: 1; }
      #${PANEL_ID} .cgt-td-debrief-meta { display: flex; flex-direction: column; gap: 2px; }
      #${PANEL_ID} .cgt-td-debrief-student { font-size: 13px; font-weight: 600; color: #f5f5f7; }
      #${PANEL_ID} .cgt-td-debrief-case { font-size: 12px; color: #a1a1a6; }
      #${PANEL_ID} .cgt-td-debrief-when { font-size: 11px; color: #6e6e73; font-family: 'JetBrains Mono', monospace; }
      #${PANEL_ID} .cgt-td-debrief-chevron { color: #6e6e73; font-size: 16px; text-align: center; }
      #${PANEL_ID} .cgt-td-debrief-detail {
        padding: 0 14px 14px; border-top: 0.5px solid rgba(255,255,255,0.05);
      }
      #${PANEL_ID} .cgt-td-debrief-section {
        padding: 12px 0; border-bottom: 0.5px dashed rgba(255,255,255,0.04);
      }
      #${PANEL_ID} .cgt-td-debrief-section:last-child { border-bottom: none; }
      #${PANEL_ID} .cgt-td-debrief-section h5 {
        margin: 0 0 6px 0; font-size: 11px; font-weight: 700;
        text-transform: uppercase; letter-spacing: 0.5px;
      }
      #${PANEL_ID} .cgt-td-debrief-section p {
        margin: 0; font-size: 12px; line-height: 1.5; color: #d1d1d6;
      }
      #${PANEL_ID} .cgt-td-debrief-section ul {
        margin: 0; padding-left: 18px; font-size: 12px; line-height: 1.55; color: #d1d1d6;
      }
      #${PANEL_ID} .cgt-td-hidden { display: none; }

      /* Vista en vivo (Sprint 5) */
      #${PANEL_ID} .cgt-td-live-dot {
        display: inline-block; width: 8px; height: 8px; border-radius: 50%;
        background: #FF453A; margin-right: 6px; vertical-align: middle;
        animation: cgt-td-live-pulse 1.5s ease-in-out infinite;
      }
      @keyframes cgt-td-live-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(255,69,58,0.7); }
        50% { box-shadow: 0 0 0 8px rgba(255,69,58,0); }
      }
      #${PANEL_ID} .cgt-td-live-grid {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 12px; padding-top: 14px;
      }
      #${PANEL_ID} .cgt-td-live-card {
        background: rgba(255,255,255,0.03); border: 0.5px solid rgba(48,209,88,0.3);
        border-radius: 10px; overflow: hidden;
      }
      #${PANEL_ID} .cgt-td-live-card header {
        display: flex; justify-content: space-between; align-items: center;
        padding: 8px 12px; background: rgba(48,209,88,0.08);
        border-bottom: 0.5px solid rgba(48,209,88,0.2);
      }
      #${PANEL_ID} .cgt-td-live-card header strong {
        font-size: 12px; color: #30D158;
      }
      #${PANEL_ID} .cgt-td-live-card header span {
        font-size: 10px; color: #6e6e73;
      }
      #${PANEL_ID} .cgt-td-live-card ul {
        margin: 0; padding: 8px 12px; list-style: none;
        max-height: 280px; overflow-y: auto;
        font-size: 11px; line-height: 1.5; color: #d1d1d6;
      }
      #${PANEL_ID} .cgt-td-live-card li { margin: 0 0 4px 0; }
      #${PANEL_ID} .cgt-td-live-card code {
        background: rgba(255,255,255,0.06); padding: 1px 4px; border-radius: 3px;
        font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #8e8e93;
        margin-right: 4px;
      }
      #${PANEL_ID} .cgt-td-live-vitals {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;
        padding: 10px 12px; background: rgba(0,0,0,0.4);
        border-bottom: 0.5px solid rgba(255,255,255,0.05);
        position: relative;
      }
      #${PANEL_ID} .cgt-td-live-vitals-stale { opacity: 0.55; }
      #${PANEL_ID} .cgt-td-live-card-ended {
        opacity: 0.6; border-color: rgba(255,255,255,0.12) !important;
      }
      #${PANEL_ID} .cgt-td-live-card-ended header {
        background: rgba(255,255,255,0.04) !important;
        border-bottom-color: rgba(255,255,255,0.06) !important;
      }
      #${PANEL_ID} .cgt-td-live-card-ended header strong { color: #8e8e93 !important; }
      #${PANEL_ID} .cgt-td-vital-cell {
        display: flex; flex-direction: column; align-items: center;
      }
      #${PANEL_ID} .cgt-td-vital-label {
        font-size: 9px; color: #6e6e73; letter-spacing: 0.5px;
        text-transform: uppercase;
      }
      #${PANEL_ID} .cgt-td-vital-value {
        font-family: 'JetBrains Mono', monospace; font-weight: 700;
        font-size: 14px; line-height: 1.1;
      }
      #${PANEL_ID} .cgt-td-vital-age {
        position: absolute; top: 3px; right: 6px; font-size: 9px;
        color: #6e6e73; font-family: 'JetBrains Mono', monospace;
      }

      /* Sparkline tendencia score IA (Gap 6) */
      #${PANEL_ID} .cgt-td-sparkline-wrap {
        margin: 14px 0;
        padding: 12px 14px;
        background: rgba(255,255,255,0.03);
        border: 0.5px solid rgba(255,255,255,0.06);
        border-radius: 10px;
      }
      #${PANEL_ID} .cgt-td-sparkline-head {
        display: flex; justify-content: space-between; align-items: center;
        font-size: 11px; color: #a1a1a6; margin-bottom: 6px;
      }
      #${PANEL_ID} .cgt-td-sparkline-head strong { color: #f5f5f7; font-size: 12px; }
      #${PANEL_ID} .cgt-td-sparkline {
        width: 100%; height: 60px; display: block;
      }

      /* Asignar alumno → mi clase */
      #${PANEL_ID} .cgt-td-assign-btn {
        background: rgba(255,255,255,0.06); color: #8e8e93;
        border: 0.5px solid rgba(255,255,255,0.1);
        width: 28px; height: 28px; border-radius: 6px;
        font-size: 14px; cursor: pointer; line-height: 1;
        transition: all 120ms;
      }
      #${PANEL_ID} .cgt-td-assign-btn:hover {
        background: rgba(48,209,88,0.15); border-color: rgba(48,209,88,0.4); color: #30D158;
      }
      #${PANEL_ID} .cgt-td-assign-btn.is-mine {
        background: rgba(48,209,88,0.2); border-color: #30D158; color: #30D158;
      }
    `;
    document.head.appendChild(style);
  }
}

export const teacherDashboard = new TeacherDashboard();
